import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ExternalLink, Pencil } from "lucide-react";
import { http } from "../lib/api";
import { PageLoading } from "../context/AuthContext";
import { BookCover, Stamp } from "../components/Bits";
import LaunchKit from "../components/LaunchKit";
import PlanCalendar from "../components/PlanCalendar";
import BookPagePanel from "../components/BookPagePanel";

const TABS = [["kit", "Launch kit"], ["plan", "30-day plan"], ["page", "Page & readers"]];

export default function Workspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "kit";
  const [book, setBook] = useState(null);

  useEffect(() => {
    http.get(`/books/${id}`).then(({ data }) => setBook(data)).catch(() => navigate("/dashboard"));
  }, [id, navigate]);

  if (!book) return <PageLoading />;
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12" data-testid="workspace-page">
      <div className="grid grid-cols-[4.5rem_1fr] sm:grid-cols-[6rem_1fr] gap-6 items-center">
        <BookCover src={book.cover_url} title={book.title} />
        <div className="min-w-0">
          <div className="flex flex-wrap gap-2 items-center">
            <Stamp active={book.published} testid="workspace-status-stamp">{book.published ? "Published" : "Draft"}</Stamp>
            <span className="label-mono">{book.genre}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-5xl tracking-tight mt-2 truncate" data-testid="workspace-book-title">{book.title}</h1>
          <div className="flex flex-wrap gap-1 mt-2 -ml-2.5">
            <Link to={`/books/${id}/edit`} className="btn-ghost" data-testid="workspace-edit-details-link"><Pencil size={12} /> Edit details</Link>
            <Link to={`/b/${book.slug}`} className="btn-ghost" data-testid="workspace-view-page-link"><ExternalLink size={12} /> View book page</Link>
          </div>
        </div>
      </div>
      <nav className="flex gap-5 sm:gap-10 mt-10 border-b border-[#E5DEC9] overflow-x-auto" data-testid="workspace-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setParams({ tab: k })} data-testid={`workspace-tab-${k}`}
            className={`pb-3 -mb-px border-b-2 whitespace-nowrap font-display text-base sm:text-xl transition-colors ${tab === k ? "border-[#C84B31] italic" : "border-transparent text-[#8E8879] hover:text-[#121210]"}`}>{label}</button>
        ))}
      </nav>
      <div className="py-10">
        {tab === "kit" && <LaunchKit book={book} setBook={setBook} />}
        {tab === "plan" && <PlanCalendar book={book} setBook={setBook} />}
        {tab === "page" && <BookPagePanel book={book} setBook={setBook} />}
      </div>
    </div>
  );
}
